import { useCallback } from 'react';

import noop from 'lodash/noop';

import {
  Box,
  IconButtonWithTooltip,
  Icon,
  useConfirmationModal,
} from '@sbv-clients/ui-shared';

import {
  useAdminLockUserAccount,
  useAdminOnboardEmployees,
  useAdminUnLockUserAccount,
} from '../../../services/admin';

interface ActionButtonsProps {
  facilityId: string;
  fullName: string;
  userId: string;
  instanceEmployeeId: number;
  isRegistered: boolean;
  isLocked: boolean;
}

const ActionButtons = ({
  facilityId,
  fullName,
  userId,
  instanceEmployeeId,
  isRegistered,
  isLocked,
}: ActionButtonsProps) => {
  const lockUserAccount = useAdminLockUserAccount();
  const unLockUserAccount = useAdminUnLockUserAccount();
  const resetPassword = useAdminOnboardEmployees();

  const confirmationModal = useConfirmationModal();

  const handleLockUserAccount = useCallback(async () => {
    await lockUserAccount.mutateAsync({ userId }).catch(noop);
  }, [lockUserAccount, userId]);

  const handleUnLockUserAccount = useCallback(async () => {
    await unLockUserAccount.mutateAsync({ userId }).catch(noop);
  }, [unLockUserAccount, userId]);

  const handleResetPassword = useCallback(async () => {
    await resetPassword
      .mutateAsync({
        onboardEmployeesRequest: {
          facilityID: facilityId,
          instanceEmployeeIDs: [instanceEmployeeId],
        },
      })
      .catch(noop);
  }, [facilityId, instanceEmployeeId, resetPassword]);

  return (
    <>
      <Box height="100%" display="flex" alignItems="center" columnGap={1}>
        {isRegistered ? (
          <>
            {isLocked ? (
              <IconButtonWithTooltip
                title="Unlock User"
                subtitle="User will have access to respective account after Lock"
                disabled={!isRegistered || unLockUserAccount.isLoading}
                loading={unLockUserAccount.isLoading}
                onClick={() => {
                  confirmationModal
                    .openModal({
                      title: 'Unlock User Account',
                      content: (
                        <>
                          Are you sure you want to unlock <b>{fullName}</b>{' '}
                          account?
                        </>
                      ),
                    })
                    .then(handleUnLockUserAccount)
                    .catch(noop);
                }}
                icon={<Icon name={'LockPersonTwoTone'} />}
              />
            ) : (
              <IconButtonWithTooltip
                title="Lock User"
                subtitle="User will have no longer access to respective account after Lock"
                disabled={!isRegistered || lockUserAccount.isLoading}
                loading={lockUserAccount.isLoading}
                onClick={() => {
                  confirmationModal
                    .openModal({
                      title: 'Lock User Account',
                      content: (
                        <>
                          Are you sure you want to lock <b>{fullName}</b>{' '}
                          account?
                        </>
                      ),
                    })
                    .then(handleLockUserAccount)
                    .catch(noop);
                }}
                icon={<Icon name={'LockOpen'} />}
              />
            )}
          </>
        ) : (
          <IconButtonWithTooltip
            title="Not Registered"
            disabled={true}
            onClick={noop}
            icon={<Icon name={'LockPersonTwoTone'} />}
          />
        )}
        <IconButtonWithTooltip
          title="Reset Password"
          subtitle="Send a password reset link to the user"
          disabled={!isRegistered || resetPassword.isLoading}
          loading={resetPassword.isLoading}
          onClick={() => {
            confirmationModal
              .openModal({
                title: 'Reset Password',
                content: (
                  <>
                    Are you sure you want reset password link to{' '}
                    <b>{fullName}</b> ?
                  </>
                ),
              })
              .then(handleResetPassword)
              .catch(noop);
          }}
          icon={<Icon name={'LockReset'} />}
        />
      </Box>
      {confirmationModal.modal}
    </>
  );
};

export default ActionButtons;
